import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, User, Building, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { getApiV1Url } from '../../config/api';

// TypeScript 인터페이스 정의
interface FormData {
  // 기본 정보 (Step 1)
  name: string;
  rootUserId: string;
  rootPassword: string;
  rootPasswordConfirm: string;
  phoneNumber: string;
  rootPinCode: string;
  phonePinCode: string;
  businessType: string;
  
  // 추가 정보 (Step 2)
  businessRegistrationNumber: string;
  companyName: string;
  companyCeoName: string;
  companyEmail: string;
  companyPhoneNumber: string;
  companyHomepageUrl: string;
  postal_code: string;
  region_code: string;
  addressRoad: string;
  addressDetail: string;
  lat: number | null;
  lon: number | null;
}

interface Errors {
  [key: string]: string;
}

interface BusinessType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
}

const AccountCreate: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [businessType, setBusinessType] = useState<string>('personal');
  const [formData, setFormData] = useState<FormData>({
    
    // 기본 정보 (Step 1)
    name: '',
    rootUserId: '',
    rootPassword: '',
    rootPasswordConfirm: '',
    phoneNumber: '',
    rootPinCode: '',
    phonePinCode: '',
    businessType: 'personal',
    
    // 추가 정보 (Step 2)
    businessRegistrationNumber: '',
    companyName: '',
    companyCeoName: '',
    companyEmail: '',
    companyPhoneNumber: '',
    companyHomepageUrl: '',
    postal_code: '',
    region_code: '',
    addressRoad: '',
    addressDetail: '',
    lat: null,
    lon: null
  });

  const [errors, setErrors] = useState<Errors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isRootPinSent, setIsRootPinSent] = useState<boolean>(false);
  const [isSendingRootPin, setIsSendingRootPin] = useState<boolean>(false);
  const [showRootPinVerification, setShowRootPinVerification] = useState<boolean>(false);
  const [isPhonePinSent, setIsPhonePinSent] = useState<boolean>(false);
  const [isSendingPhonePin, setIsSendingPhonePin] = useState<boolean>(false);
  const [showPhonePinVerification, setShowPhonePinVerification] = useState<boolean>(false);
  const [isRootPinVerified, setIsRootPinVerified] = useState<boolean>(false);
  const [isPhonePinVerified, setIsPhonePinVerified] = useState<boolean>(false);
  const [isVerifyingRootPin, setIsVerifyingRootPin] = useState<boolean>(false);
  const [isVerifyingPhonePin, setIsVerifyingPhonePin] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState<boolean>(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    // 숫자만 허용 (하이픈 제외)
    const numericValue = value.replace(/[^0-9]/g, '');
    setFormData(prev => ({
      ...prev,
      [name]: numericValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBusinessTypeChange = (type: string): void => {
    setBusinessType(type);
    setFormData(prev => ({
      ...prev,
      businessType: type
    }));
  };

  const validateStep1 = (): boolean => {
    const newErrors: Errors = {};
    
    if (!formData.name || formData.name.length > 20) {
      newErrors.name = '계정명은 필수이며 20자 이내여야 합니다';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.rootUserId || !emailRegex.test(formData.rootUserId)) {
      newErrors.rootUserId = '유효한 이메일 주소를 입력해주세요';
    }
    
    if (!formData.rootPassword || formData.rootPassword.length < 8 || formData.rootPassword.length > 20) {
      newErrors.rootPassword = '비밀번호는 8-20자 사이여야 합니다';
    }
    
    if (formData.rootPassword !== formData.rootPasswordConfirm) {
      newErrors.rootPasswordConfirm = '비밀번호가 일치하지 않습니다';
    }
    
    const phoneRegex = /^010-?\d{4}-?\d{4}$/;
    if (!formData.phoneNumber || !phoneRegex.test(formData.phoneNumber.replace(/-/g, ''))) {
      newErrors.phoneNumber = '유효한 휴대폰 번호를 입력해주세요 (010-XXXX-XXXX)';
    }
    
    if (!formData.rootPinCode || formData.rootPinCode.length !== 6) {
      newErrors.rootPinCode = 'PIN 코드는 6자리여야 합니다';
    }
    
    if (!formData.phonePinCode || formData.phonePinCode.length !== 6) {
      newErrors.phonePinCode = '휴대폰 PIN은 6자리여야 합니다';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Errors = {};
    
    if (businessType === 'business') {
      if (!formData.businessRegistrationNumber) {
        newErrors.businessRegistrationNumber = '사업자번호는 필수입니다';
      }
      if (!formData.companyName) {
        newErrors.companyName = '회사명은 필수입니다';
      }
      if (!formData.companyCeoName) {
        newErrors.companyCeoName = '대표자명은 필수입니다';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      // PIN 발송 상태 확인 (기본 유효성 검사보다 먼저)
      if (!isRootPinSent) {
        alert('이메일 PIN 발송을 먼저 해주세요.');
        return;
      }
      
      if (!isPhonePinSent) {
        alert('휴대폰 PIN 발송을 먼저 해주세요.');
        return;
      }
      
      // PIN 확인 상태 확인
      if (!isRootPinVerified) {
        alert('이메일 PIN 확인을 먼저 해주세요.');
        return;
      }
      
      if (!isPhonePinVerified) {
        alert('휴대폰 PIN 확인을 먼저 해주세요.');
        return;
      }
      
      // 기본 유효성 검사
      if (validateStep1()) {
        setCurrentStep(2);
        window.scrollTo(0, 0);
      }
    }
  };

  const handleBack = (): void => {
    setCurrentStep(1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateStep2()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // API 스펙에 맞게 데이터 준비 (rootPasswordConfirm 제거)
      const { rootPasswordConfirm, ...apiData } = formData;
      
      const response = await fetch(getApiV1Url('/accounts'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });

      if (response.ok) {
        alert('계정이 성공적으로 생성되었습니다!');
        // 성공 시 로그인 페이지로 이동
        navigate('/auth/login');
      } else {
        throw new Error('계정 생성에 실패했습니다.');
      }
    } catch (error) {
      alert('계정 생성에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const searchAddress = (): void => {
    // 실제로는 카카오 주소 API 등을 사용
    alert('주소 검색 API를 연동해주세요 (Daum Postcode API 추천)');
  };

  const sendRootPinCode = async (): Promise<void> => {
    if (!formData.rootUserId) {
      setErrors(prev => ({
        ...prev,
        rootUserId: '이메일을 먼저 입력해주세요'
      }));
      return;
    }

    setIsSendingRootPin(true);
    
    try {
      const response = await fetch(getApiV1Url('/accounts/pin/send'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sendType: 'EMAIL',
          destination: formData.rootUserId
        })
      });

      if (response.ok) {
        // 성공 시
        setIsRootPinSent(true);
        setShowRootPinVerification(true);
        setErrors(prev => ({
          ...prev,
          rootUserId: ''
        }));
        
        // 성공 메시지 표시
        alert('PIN 코드가 이메일로 발송되었습니다.');
      } else {
        // 에러 응답 처리
        const errorData = await response.json();
        const errorMessage = errorData.errors?.error?.[0]?.message || 'PIN 발송에 실패했습니다.';
        setErrors(prev => ({
          ...prev,
          rootUserId: errorMessage
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        rootUserId: 'PIN 발송에 실패했습니다. 다시 시도해주세요.'
      }));
    } finally {
      setIsSendingRootPin(false);
    }
  };

  const sendPhonePinCode = async (): Promise<void> => {
    if (!formData.phoneNumber) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: '휴대폰 번호를 먼저 입력해주세요'
      }));
      return;
    }

    setIsSendingPhonePin(true);
    
    try {
      const response = await fetch(getApiV1Url('/accounts/pin/send'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sendType: 'SMS',
          destination: formData.phoneNumber
        })
      });

      if (response.ok) {
        // 성공 시
        setIsPhonePinSent(true);
        setShowPhonePinVerification(true);
        setErrors(prev => ({
          ...prev,
          phoneNumber: ''
        }));
        
        // 성공 메시지 표시
        alert('PIN 코드가 휴대폰으로 발송되었습니다.');
      } else {
        // 에러 응답 처리
        const errorData = await response.json();
        const errorMessage = errorData.errors?.error?.[0]?.message || 'PIN 발송에 실패했습니다.';
        setErrors(prev => ({
          ...prev,
          phoneNumber: errorMessage
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: 'PIN 발송에 실패했습니다. 다시 시도해주세요.'
      }));
    } finally {
      setIsSendingPhonePin(false);
    }
  };

  const verifyPinCode = async (
    sendType: 'EMAIL' | 'SMS',
    destination: string,
    pinCode: string
  ): Promise<{ verified: boolean; message?: string }> => {
    const response = await fetch(getApiV1Url('/accounts/pin/verify'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sendType,
        destination,
        pinCode,
      }),
    });

    const result = await response.json().catch(() => ({}));

    if (result.errors?.error && result.errors.error.length > 0) {
      const message = result.errors.error
        .map((error: { message?: string }) => error.message)
        .join(', ');
      return { verified: false, message };
    }

    if (!response.ok) {
      return {
        verified: false,
        message: result.message || 'PIN 코드가 올바르지 않습니다.',
      };
    }

    if (result.data?.exists === true) {
      return { verified: true };
    }

    return { verified: false, message: 'PIN 코드가 올바르지 않습니다.' };
  };

  const verifyRootPinCode = async (): Promise<void> => {
    if (!formData.rootPinCode.trim()) {
      alert('PIN 코드를 입력해주세요.');
      return;
    }

    setIsVerifyingRootPin(true);
    try {
      const { verified, message } = await verifyPinCode(
        'EMAIL',
        formData.rootUserId,
        formData.rootPinCode
      );

      if (verified) {
        setIsRootPinVerified(true);
        alert('이메일 PIN 코드가 확인되었습니다.');
      } else {
        alert(`PIN 확인 실패: ${message || 'PIN 코드가 올바르지 않습니다.'}`);
      }
    } catch (error) {
      console.error('PIN verify error:', error);
      alert('PIN 확인 중 오류가 발생했습니다.');
    } finally {
      setIsVerifyingRootPin(false);
    }
  };

  const verifyPhonePinCode = async (): Promise<void> => {
    if (!formData.phonePinCode.trim()) {
      alert('PIN 코드를 입력해주세요.');
      return;
    }

    setIsVerifyingPhonePin(true);
    try {
      const { verified, message } = await verifyPinCode(
        'SMS',
        formData.phoneNumber,
        formData.phonePinCode
      );

      if (verified) {
        setIsPhonePinVerified(true);
        alert('휴대폰 PIN 코드가 확인되었습니다.');
      } else {
        alert(`PIN 확인 실패: ${message || 'PIN 코드가 올바르지 않습니다.'}`);
      }
    } catch (error) {
      console.error('Phone PIN verify error:', error);
      alert('PIN 확인 중 오류가 발생했습니다.');
    } finally {
      setIsVerifyingPhonePin(false);
    }
  };

  const resetRootPin = (): void => {
    setIsRootPinSent(false);
    setIsRootPinVerified(false);
    setShowRootPinVerification(false);
    setFormData(prev => ({ ...prev, rootPinCode: '' }));
  };

  const resetPhonePin = (): void => {
    setIsPhonePinSent(false);
    setIsPhonePinVerified(false);
    setShowPhonePinVerification(false);
    setFormData(prev => ({ ...prev, phonePinCode: '' }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div 
            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          >
            <img src="/flos-icon.svg" alt="Flos" className="w-8 h-8 mr-2" />
            <span className="text-xl font-semibold text-gray-900">Flos</span>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep >= 1 ? 'bg-gray-900 text-white' : 'bg-gray-300 text-gray-600'}`}>
              {currentStep > 1 ? <Check className="w-5 h-5" /> : '1'}
            </div>
            <span className="ml-3 font-medium text-gray-900">기본 정보</span>
          </div>
          <div className="w-24 h-1 mx-4 bg-gray-300">
            <div className={`h-full ${currentStep >= 2 ? 'bg-gray-900' : 'bg-gray-300'} transition-all duration-300`} />
          </div>
          <div className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep >= 2 ? 'bg-gray-900 text-white' : 'bg-gray-300 text-gray-600'}`}>
              {currentStep > 2 ? <Check className="w-5 h-5" /> : '2'}
            </div>
            <span className="ml-3 font-medium text-gray-900">추가 정보</span>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div>
            {currentStep === 1 && (
              <div className="p-6 space-y-6">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">기본 정보 입력</h2>
                  <p className="text-gray-600">계정 생성을 위한 필수 정보를 입력해주세요</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      계정명 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.accountName ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="계정 이름을 입력하세요"
                      maxLength={20}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-500 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      루트 사용자 ID (이메일) <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        name="rootUserId"
                        value={formData.rootUserId}
                        onChange={handleInputChange}
                        readOnly={isRootPinVerified}
                        className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.rootUserId ? 'border-red-500' : 'border-gray-300'} ${isRootPinVerified ? 'bg-gray-100' : ''}`}
                        placeholder="admin@example.com"
                        maxLength={50}
                      />
                      <button
                        type="button"
                        onClick={isRootPinVerified ? resetRootPin : sendRootPinCode}
                        disabled={isSendingRootPin || (isRootPinSent && !isRootPinVerified)}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          isSendingRootPin 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : isRootPinVerified
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : isRootPinSent
                            ? 'bg-green-100 text-green-700 border border-green-300'
                            : 'bg-gray-900 text-white hover:bg-gray-800'
                        }`}
                      >
                        {isSendingRootPin ? '발송중...' : isRootPinVerified ? '재설정' : isRootPinSent ? '발송완료' : 'PIN 발송'}
                      </button>
                    </div>
                    {errors.rootUserId && (
                      <p className="mt-1 text-sm text-red-500 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.rootUserId}
                      </p>
                    )}
                  </div>

                  {/* PIN 확인 입력란 - 숨겨져 있다가 PIN 발송 성공 시 표시 */}
                  {showRootPinVerification && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <label className="block text-sm font-medium text-blue-700 mb-2">
                        이메일로 발송된 PIN 코드를 입력하세요 <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          name="rootPinCode"
                          value={formData.rootPinCode}
                          onChange={handleInputChange}
                          className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.rootPinCode ? 'border-red-500' : 'border-blue-300'}`}
                          placeholder="6자리 PIN 코드"
                          maxLength={6}
                        />
                        <button
                          type="button"
                          onClick={verifyRootPinCode}
                          disabled={isVerifyingRootPin || isRootPinVerified || !formData.rootPinCode.trim()}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            isVerifyingRootPin || !formData.rootPinCode.trim()
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : isRootPinVerified
                              ? 'bg-green-100 text-green-700 border border-green-300'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {isVerifyingRootPin ? '확인중...' : isRootPinVerified ? '확인완료' : 'PIN 확인'}
                        </button>
                      </div>
                      {errors.rootPinCode && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.rootPinCode}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-blue-600">
                        {formData.rootUserId}로 발송된 6자리 PIN 코드를 입력해주세요.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        비밀번호 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="rootPassword"
                          value={formData.rootPassword}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.rootPassword ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="8-20자"
                          maxLength={20}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      {errors.rootPassword && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.rootPassword}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        비밀번호 확인 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswordConfirm ? "text" : "password"}
                          name="rootPasswordConfirm"
                          value={formData.rootPasswordConfirm}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.rootPasswordConfirm ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="비밀번호 재입력"
                          maxLength={20}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        >
                          {showPasswordConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      {errors.rootPasswordConfirm && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.rootPasswordConfirm}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      휴대폰 번호 <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleNumberInputChange}
                        readOnly={isPhonePinVerified}
                        className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} ${isPhonePinVerified ? 'bg-gray-100' : ''}`}
                        placeholder="01012345678 (숫자만 입력)"
                        maxLength={20}
                      />
                      <button
                        type="button"
                        onClick={isPhonePinVerified ? resetPhonePin : sendPhonePinCode}
                        disabled={isSendingPhonePin || (isPhonePinSent && !isPhonePinVerified)}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          isSendingPhonePin 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : isPhonePinVerified
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : isPhonePinSent
                            ? 'bg-green-100 text-green-700 border border-green-300'
                            : 'bg-gray-900 text-white hover:bg-gray-800'
                        }`}
                      >
                        {isSendingPhonePin ? '발송중...' : isPhonePinVerified ? '재설정' : isPhonePinSent ? '발송완료' : 'PIN 발송'}
                      </button>
                    </div>
                    {errors.phoneNumber && (
                      <p className="mt-1 text-sm text-red-500 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.phoneNumber}
                      </p>
                    )}
                  </div>

                  {/* 휴대폰 PIN 확인 입력란 - 숨겨져 있다가 PIN 발송 성공 시 표시 */}
                  {showPhonePinVerification && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <label className="block text-sm font-medium text-blue-700 mb-2">
                        휴대폰으로 발송된 PIN 코드를 입력하세요 <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          name="phonePinCode"
                          value={formData.phonePinCode}
                          onChange={handleInputChange}
                          className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.phonePinCode ? 'border-red-500' : 'border-blue-300'}`}
                          placeholder="6자리 PIN 코드"
                          maxLength={6}
                        />
                        <button
                          type="button"
                          onClick={verifyPhonePinCode}
                          disabled={isVerifyingPhonePin || isPhonePinVerified || !formData.phonePinCode.trim()}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            isVerifyingPhonePin || !formData.phonePinCode.trim()
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : isPhonePinVerified
                              ? 'bg-green-100 text-green-700 border border-green-300'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {isVerifyingPhonePin ? '확인중...' : isPhonePinVerified ? '확인완료' : 'PIN 확인'}
                        </button>
                      </div>
                      {errors.phonePinCode && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.phonePinCode}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-blue-600">
                        {formData.phoneNumber}로 발송된 6자리 PIN 코드를 입력해주세요.
                      </p>
                    </div>
                  )}


                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      계정 유형 <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => handleBusinessTypeChange('personal')}
                        className={`p-4 border rounded-lg flex items-center justify-center transition-colors ${
                          businessType === 'personal'
                            ? 'border-gray-900 bg-gray-50'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <User className="w-5 h-5 mr-2" />
                        <span className="font-medium">개인</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBusinessTypeChange('business')}
                        className={`p-4 border rounded-lg flex items-center justify-center transition-colors ${
                          businessType === 'business'
                            ? 'border-gray-900 bg-gray-50'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Building className="w-5 h-5 mr-2" />
                        <span className="font-medium">사업자</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    계정 생성 취소
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center"
                  >
                    다음 단계
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="p-6 space-y-6">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">추가 정보 입력</h2>
                  <p className="text-gray-600">
                    {businessType === 'business' ? '사업자 정보를 입력해주세요' : '선택 정보를 입력해주세요 (건너뛸 수 있습니다)'}
                  </p>
                </div>

                <div className="space-y-4">
                  {businessType === 'business' && (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <p className="text-sm text-blue-800">
                          사업자 계정으로 가입하시면 세금계산서 발행 및 기업 전용 기능을 이용하실 수 있습니다.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          사업자번호 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="businessRegistrationNumber"
                          value={formData.businessRegistrationNumber}
                          onChange={handleNumberInputChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.businessRegistrationNumber ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="1234567890 (숫자만 입력)"
                          maxLength={20}
                        />
                        {errors.businessRegistrationNumber && (
                          <p className="mt-1 text-sm text-red-500 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.businessRegistrationNumber}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            회사명 <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.companyName ? 'border-red-500' : 'border-gray-300'}`}
                            placeholder="(주)회사명"
                            maxLength={50}
                          />
                          {errors.companyName && (
                            <p className="mt-1 text-sm text-red-500 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.companyName}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            대표자명 <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="companyCeoName"
                            value={formData.companyCeoName}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${errors.companyCeoName ? 'border-red-500' : 'border-gray-300'}`}
                            placeholder="홍길동"
                            maxLength={50}
                          />
                          {errors.companyCeoName && (
                            <p className="mt-1 text-sm text-red-500 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.companyCeoName}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            회사 이메일
                          </label>
                          <input
                            type="email"
                            name="companyEmail"
                            value={formData.companyEmail}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                            placeholder="company@example.com"
                            maxLength={100}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            회사 전화번호
                          </label>
                          <input
                            type="tel"
                            name="companyPhoneNumber"
                            value={formData.companyPhoneNumber}
                            onChange={handleNumberInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                            placeholder="0212345678 (숫자만 입력)"
                            maxLength={20}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          홈페이지 URL
                        </label>
                        <input
                          type="url"
                          name="companyHomepageUrl"
                          value={formData.companyHomepageUrl}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                          placeholder="https://example.com"
                          maxLength={100}
                        />
                      </div>
                    </>
                  )}

                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="text-lg font-medium text-gray-900">주소 정보 (선택)</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          우편번호
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            name="postal_code"
                            value={formData.postal_code}
                            onChange={handleInputChange}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                            placeholder="12345"
                            maxLength={10}
                            readOnly
                          />
                          <button
                            type="button"
                            onClick={searchAddress}
                            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            주소 검색
                          </button>
                        </div>
                      </div>

                      <div className="hidden">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          지역 코드
                        </label>
                        <input
                          type="text"
                          name="region_code"
                          value={formData.region_code}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                          placeholder="지역 코드"
                          maxLength={20}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        도로명 주소
                      </label>
                      <input
                        type="text"
                        name="addressRoad"
                        value={formData.addressRoad}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                        placeholder="서울특별시 강남구 테헤란로 123"
                        maxLength={200}
                        readOnly
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        상세 주소
                      </label>
                      <input
                        type="text"
                        name="addressDetail"
                        value={formData.addressDetail}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                        placeholder="○○빌딩 5층"
                        maxLength={200}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-6 border-t">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <ChevronLeft className="mr-2 w-5 h-5" />
                    이전 단계
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={`px-6 py-2 bg-gray-900 text-white rounded-lg transition-colors flex items-center ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        처리 중...
                      </>
                    ) : (
                      <>
                        계정 생성
                        <Check className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 p-4 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>도움이 필요하신가요?</strong> 계정 생성 과정에서 문제가 발생하면{' '}
            <a href="#" className="text-blue-600 hover:underline">고객 지원팀</a>에 문의하세요.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountCreate;